import { getGraphemes, isEmojiGrapheme } from './emojiUtils';

const MAX_INITIALS_PER_WORD = 2;

/**
 * Returns non-emoji characters from a string.
 */
const getContentChars = (word: string): string[] => {
  return getGraphemes(word)
    .filter((grapheme) => !isEmojiGrapheme(grapheme))
    .flatMap((grapheme) => [...grapheme]);
};

/**
 * Returns the first emoji grapheme found in a string,
 * or an empty string if none is found.
 */
const getFirstEmoji = (word: string): string => {
  for (const grapheme of getGraphemes(word)) {
    if (isEmojiGrapheme(grapheme)) {
      return grapheme;
    }
  }
  return '';
};

/**
 * Derives raw avatar initials from a display name.
 *
 * Content is defined as any non-emoji character. Emojis are only used as a
 * fallback when no content characters are available.
 */
const deriveInitials = (fullName: string): string => {
  if (!fullName) {
    return '';
  }

  const names = fullName.split(' ').filter(Boolean);

  if (names.length === 0) {
    return '';
  }

  if (names.length === 1) {
    const content = getContentChars(names[0]);

    if (content.length > 0) {
      return content.slice(0, MAX_INITIALS_PER_WORD).join('');
    }

    return getFirstEmoji(names[0]);
  }

  const contentNames = names.filter((name) => getContentChars(name).length > 0);

  if (contentNames.length >= 2) {
    const first = getContentChars(contentNames[0])[0];
    const last = getContentChars(contentNames[contentNames.length - 1])[0];
    return first + last;
  }

  if (contentNames.length === 1) {
    return getContentChars(contentNames[0])
      .slice(0, MAX_INITIALS_PER_WORD)
      .join('');
  }

  return getFirstEmoji(fullName);
};

/**
 * Creates avatar initials from a display name.
 *
 * Returns uppercase letters for standard name content so avatar consumers do
 * not need to apply casing themselves. Emojis and symbols are left unchanged.
 */
export const formatUserInitials = (fullName: string): string => {
  const initials = deriveInitials(fullName);

  if (!initials) {
    return '';
  }

  return initials.toUpperCase();
};
